import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const COUNTER_LABELS = { ferie: "Ferie", rol: "ROL / Permessi", ex_fest: "Ex festività" };
export const CHECK_LABELS = {
  employee: "Dipendente", period: "Periodo di competenza", ferie: "Saldo ferie", rol: "Saldo ROL",
  ex_fest: "Saldo ex festività", unit: "Unità di misura", included_movements: "Rettifiche e conguagli già inclusi",
};
const UNIT = { days: "gg", hours: "h" };
const sel = "h-8 rounded-md border border-suite-neutral-300 px-2 text-xs";
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));

export const emptyChecklist = () => Object.fromEntries(Object.keys(CHECK_LABELS).map((k) => [k, { status: "pending", note: "" }]));

function CounterRow({ c, eng, value, onChange, preview }) {
  const v = value;
  const payroll = num(v.payroll_value);
  const converts = v.payroll_unit !== eng.unit;
  const divisor = converts ? num(v.divisor) ?? (preview.divisor_certain ? preview.hours_per_day : null) : 1;
  const verified = payroll === null || divisor === null ? null : converts ? (eng.unit === "days" ? payroll / divisor : payroll * divisor) : payroll;
  const diff = verified === null ? null : verified - eng.value;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <tr className="border-t border-suite-neutral-100 align-top" data-testid={`cb-counter-${c}`}>
      <td className="px-2 py-2 font-medium">{COUNTER_LABELS[c]}</td>
      <td className="px-2 py-2" data-testid={`cb-engine-${c}`}>{eng.value.toFixed(3)} {UNIT[eng.unit]}</td>
      <td className="px-2 py-2"><div className="flex gap-1">
        <Input className="h-8 w-24 text-xs" value={v.payroll_value} onChange={set("payroll_value")} placeholder="Valore" data-testid={`cb-payroll-${c}`} />
        <select className={sel} value={v.payroll_unit} onChange={set("payroll_unit")} data-testid={`cb-unit-${c}`}><option value="days">giorni</option><option value="hours">ore</option></select>
      </div>
        {converts && <div className="mt-1 space-y-1" data-testid={`cb-conversion-${c}`}>
          <Input className="h-8 text-xs" value={v.divisor} onChange={set("divisor")} placeholder={preview.divisor_certain ? `Divisore (profilo: ${preview.hours_per_day} h/g)` : "Divisore obbligatorio"} data-testid={`cb-divisor-${c}`} />
          <Input className="h-8 text-xs" value={v.divisor_criterion} onChange={set("divisor_criterion")} placeholder="Criterio e fonte del divisore" data-testid={`cb-criterion-${c}`} />
        </div>}
      </td>
      <td className="px-2 py-2" data-testid={`cb-verified-${c}`}>{verified === null ? "—" : `${verified.toFixed(3)} ${UNIT[eng.unit]}`}</td>
      <td className={`px-2 py-2 font-semibold ${diff && Math.abs(diff) > 1e-6 ? "text-red-700" : "text-emerald-700"}`} data-testid={`cb-diff-${c}`}>{diff === null ? "—" : `${diff >= 0 ? "+" : ""}${diff.toFixed(3)}`}</td>
      <td className="px-2 py-2 space-y-1">
        <Input className="h-8 text-xs" value={v.difference_reason} onChange={set("difference_reason")} placeholder={diff && Math.abs(diff) > 1e-6 ? "Motivazione obbligatoria" : "Motivazione (se differenza)"} data-testid={`cb-reason-${c}`} />
        {verified !== null && verified < 0 && <label className="flex items-center gap-1 text-[11px] text-red-800"><input type="checkbox" checked={v.negative_confirmed} onChange={set("negative_confirmed")} data-testid={`cb-negative-${c}`} />Confermo saldo negativo verificato</label>}
      </td>
    </tr>
  );
}

function Checklist({ value, onChange }) {
  return (
    <div className="grid md:grid-cols-2 gap-2" data-testid="cb-checklist">
      {Object.entries(CHECK_LABELS).map(([k, label]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="w-52">{label}</span>
          <select className={sel} value={value[k].status} onChange={(e) => onChange({ ...value, [k]: { ...value[k], status: e.target.value } })} data-testid={`cb-check-${k}`}>
            <option value="pending">Da verificare</option><option value="verified">Verificata</option><option value="inconsistent">Incongruenza</option>
          </select>
          {value[k].status === "inconsistent" && <Input className="h-8 text-xs" value={value[k].note} onChange={(e) => onChange({ ...value, [k]: { ...value[k], note: e.target.value } })} placeholder="Descrivere" data-testid={`cb-check-note-${k}`} />}
        </div>
      ))}
    </div>
  );
}

export function CertifiedBaselineForm({ employeeId, onSaved, checklist, setChecklist }) {
  const [month, setMonth] = useState("");
  const [preview, setPreview] = useState(null);
  const [counters, setCounters] = useState({});
  const [doc, setDoc] = useState({ type: "cedolino", period: "", reference: "", notes: "" });
  useEffect(() => {
    setPreview(null);
    if (!employeeId || !month) return;
    const [y, m] = month.split("-").map(Number);
    api.get(`/counters-consolidation/employees/${employeeId}/certified-baseline/preview`, { params: { year: y, month: m } })
      .then(({ data }) => {
        setPreview(data);
        setCounters(Object.fromEntries(Object.entries(data.counters).map(([c, e]) => [c, { payroll_value: "", payroll_unit: e.unit, divisor: "", divisor_criterion: "", difference_reason: "", negative_confirmed: false }])));
        setDoc((d) => ({ ...d, period: `${String(m).padStart(2, "0")}/${y}` }));
      }).catch((e) => toast.error(apiErr(e)));
  }, [employeeId, month]);
  const save = async () => {
    const [y, m] = month.split("-").map(Number);
    const body = { year: y, month: m, document: doc, checklist, counters: Object.fromEntries(Object.entries(counters).map(([c, v]) => [c, {
      payroll_value: num(v.payroll_value), payroll_unit: v.payroll_unit, divisor: num(v.divisor), divisor_criterion: v.divisor_criterion || null,
      difference_reason: v.difference_reason || null, negative_confirmed: v.negative_confirmed }])) };
    try { await api.post(`/counters-consolidation/employees/${employeeId}/certified-baselines`, body); toast.success("Baseline verificata registrata"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const complete = preview && Object.values(counters).every((v) => num(v.payroll_value) !== null) && doc.reference.trim().length >= 2;
  return (
    <div className="space-y-3 rounded-lg border border-suite-neutral-200 bg-white p-3" data-testid="certified-baseline-form">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold">Mese di taglio</span>
        <Input type="month" className="h-8 w-40 text-xs" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="cb-cut-month" />
        {preview && <span className="text-suite-neutral-600" data-testid="cb-cut-date">Data di taglio {preview.cut_date} · Ferie in {preview.vacation_unit === "days" ? "giorni" : "ore"} · {preview.hours_per_day} h/g{preview.hour_profile.present ? "" : " (profilo orario assente)"}</span>}
      </div>
      {preview?.cut_errors?.length > 0 && <div className="text-xs text-red-700" data-testid="cb-cut-errors">{preview.cut_errors.join(" ")}</div>}
      {preview && (
        <>
          <table className="w-full text-xs">
            <thead className="text-suite-neutral-500"><tr>{["Contatore", "Saldo GI Presenze", "Saldo cedolino", "Saldo verificato", "Differenza", "Motivazione"].map((h) => <th key={h} className="px-2 py-1 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>{Object.entries(preview.counters).map(([c, eng]) => counters[c] && <CounterRow key={c} c={c} eng={eng} preview={preview} value={counters[c]} onChange={(v) => setCounters({ ...counters, [c]: v })} />)}</tbody>
          </table>
          <div className="grid md:grid-cols-4 gap-2 text-xs" data-testid="cb-document">
            <select className={sel} value={doc.type} onChange={(e) => setDoc({ ...doc, type: e.target.value })} data-testid="cb-doc-type"><option value="cedolino">Cedolino</option><option value="prospetto">Prospetto</option></select>
            <Input className="h-8 text-xs" value={doc.period} onChange={(e) => setDoc({ ...doc, period: e.target.value })} placeholder="Periodo di competenza" data-testid="cb-doc-period" />
            <Input className="h-8 text-xs" value={doc.reference} onChange={(e) => setDoc({ ...doc, reference: e.target.value })} placeholder="Riferimento (es. LUL n.)" data-testid="cb-doc-reference" />
            <Input className="h-8 text-xs" value={doc.notes} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} placeholder="Note" data-testid="cb-doc-notes" />
          </div>
          <Checklist value={checklist} onChange={setChecklist} />
          <Button size="sm" onClick={save} disabled={!complete || preview.cut_errors.length > 0} data-testid="cb-save-btn">Registra proposta di baseline verificata</Button>
        </>
      )}
    </div>
  );
}
